import { useState, useEffect } from "react";
import { Navigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Container,
  Grid,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DataObjectIcon from "@mui/icons-material/DataObject";
import GavelIcon from "@mui/icons-material/Gavel";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ArticleIcon from "@mui/icons-material/Article";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import FolderOpenOutlinedIcon from "@mui/icons-material/FolderOpenOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import SettingsSuggestOutlinedIcon from "@mui/icons-material/SettingsSuggestOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import DataGridCustom from "../components/General/DatagridCustom";
import ActionHeaderCard from "../components/General/ActionHeaderCard";
import AppDialog from "../components/General/Dialog/AppDialog";
import ConfirmDialog from "../components/General/Dialog/ConfirmDialog";
import { useAuth } from "../context/AuthContext";

import {
  getAllSourceTypeDefinitions,
  createSourceTypeDefinition,
  updateSourceTypeDefinition,
  deleteSourceTypeDefinition,
} from "../services/AttachmentSourceTypeService";

const ICON_OPTIONS = [
  { value: "gavel",         label: "Martillo (Título)",       icon: <GavelIcon fontSize="small" /> },
  { value: "upload_file",   label: "Subir archivo (Decreto)", icon: <UploadFileIcon fontSize="small" /> },
  { value: "article",       label: "Artículo (Certificado)",  icon: <ArticleIcon fontSize="small" /> },
  { value: "auto_fix_high", label: "Varita (Cédula firmada)", icon: <AutoFixHighIcon fontSize="small" /> },
  { value: "attach_file",   label: "Clip (Genérico)",         icon: <AttachFileIcon fontSize="small" /> },
];

// Orígenes del archivo, explicados en lenguaje común. Los dos primeros
// cubren los casos del día a día; el tercero requiere conocer el modelo.
const ORIGINS = [
  {
    value: "legal_file_document",
    label: "Documento cargado en el expediente",
    hint: "Un PDF que está en los adjuntos de cada expediente (como la Planilla de Aportes). Se adjunta el más reciente.",
    icon: <FolderOpenOutlinedIcon />,
  },
  {
    value: "lawyer_field",
    label: "Archivo de la ficha del abogado",
    hint: "Un archivo que se carga una sola vez en el abogado (como el Decreto) y sirve para todos sus expedientes.",
    icon: <BadgeOutlinedIcon />,
  },
  {
    value: "advanced",
    label: "Otro archivo generado por jex (avanzado)",
    hint: "Demandas, cédulas u otros archivos internos. Requiere indicar una ruta técnica: consultá con sistemas.",
    icon: <SettingsSuggestOutlinedIcon />,
  },
];

const ADVANCED_STRATEGIES = [
  { value: "legal_file_relation",       label: "Relación del expediente" },
  { value: "legal_file_relation_param", label: "Relación del expediente + elegir cédula" },
];

const originOf = (strategy) =>
  strategy === "legal_file_document" || strategy === "lawyer_field" ? strategy : "advanced";

const EMPTY_FORM = {
  source_type: "", label: "", type: "", icon_key: "attach_file", description: "",
  is_active: true, sort_order: 0, resolver_strategy: "legal_file_document", relation_path: "",
};

const ORIGIN_CHIP = {
  lawyer_field:        { label: "Ficha del abogado",      color: "secondary" },
  legal_file_document: { label: "Adjuntos del expediente", color: "warning" },
  advanced:            { label: "Generado por jex",        color: "info" },
};

function AttachmentSourceTypesContent() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [lawyerFields, setLawyerFields] = useState([]);
  const [typeOptions, setTypeOptions] = useState([]);
  const [showTechnical, setShowTechnical] = useState(false);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getAllSourceTypeDefinitions();
      setRows(res?.data?.data ?? []);
      setLawyerFields(res?.data?.lawyer_fields ?? []);
      setTypeOptions(res?.data?.types ?? []);
    } catch {
      setError("Error al cargar las definiciones.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setFormErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const origin = originOf(form.resolver_strategy);

  const setOrigin = (value) => {
    setForm((prev) => ({
      ...prev,
      resolver_strategy: value === "advanced" ? "legal_file_relation" : value,
      relation_path: "",
      type: value === "lawyer_field" && !prev.type ? "abogado" : prev.type,
    }));
    setFormErrors({});
    if (value === "advanced") setShowTechnical(true);
  };

  const validate = () => {
    const e = {};
    if (!form.label.trim()) e.label = "Escribí el nombre del archivo.";
    if (origin === "lawyer_field" && !form.relation_path) {
      e.relation_path = "Elegí qué archivo del abogado se adjunta.";
    }
    if (origin === "advanced" && !form.relation_path.trim()) {
      e.relation_path = "Indicá la ruta del archivo.";
    }
    return e;
  };

  const openCreate = () => {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setFormErrors({});
    setShowTechnical(false);
    setDialogOpen(true);
  };
  const openEdit = (row) => {
    setEditRow(row);
    setForm({
      source_type: row.source_type, label: row.label, type: row.type ?? "",
      icon_key: row.icon_key ?? "attach_file", description: row.description ?? "",
      is_active: row.is_active, sort_order: row.sort_order ?? 0,
      resolver_strategy: row.resolver_strategy ?? "legal_file_relation",
      relation_path: row.relation_path ?? "",
    });
    setFormErrors({});
    setShowTechnical(originOf(row.resolver_strategy) === "advanced");
    setDialogOpen(true);
  };

  const handleSave = async () => {
    const e = validate();
    if (Object.keys(e).length > 0) { setFormErrors(e); return; }
    setSaving(true);
    setError("");
    try {
      if (editRow) await updateSourceTypeDefinition(editRow.id, form);
      else await createSourceTypeDefinition(form);
      setDialogOpen(false);
      setSuccess(editRow ? "Fuente actualizada." : "Fuente creada. Ya aparece en la lista de archivos del escrito.");
      await load();
    } catch (err) {
      const fieldErrors = err?.response?.data?.errors;
      if (fieldErrors) {
        setFormErrors(Object.fromEntries(
          Object.entries(fieldErrors).map(([k, v]) => [k, Array.isArray(v) ? v[0] : v]),
        ));
      } else {
        setError(err?.response?.data?.message ?? "Error al guardar.");
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await deleteSourceTypeDefinition(rowToDelete.id);
      setConfirmDeleteOpen(false);
      setRowToDelete(null);
      setSuccess("Fuente eliminada.");
      await load();
    } catch (err) {
      setError(err?.response?.data?.message ?? "Error al eliminar.");
    } finally {
      setDeleting(false);
    }
  };

  const lawyerFieldLabel = (field) =>
    lawyerFields.find((f) => f.value === field)?.label ?? field;

  const columns = [
    { field: "label", headerName: "Nombre", flex: 1.2, minWidth: 200 },
    { field: "type_label", headerName: "Tipo", flex: 1, minWidth: 170 },
    {
      field: "resolver_strategy", headerName: "De dónde sale", flex: 1.3, minWidth: 240,
      renderCell: ({ row }) => {
        const key = originOf(row.resolver_strategy);
        const cfg = ORIGIN_CHIP[key];
        const detail = key === "lawyer_field" ? lawyerFieldLabel(row.relation_path) : null;
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: "100%" }}>
            <Chip label={cfg.label} size="small" color={cfg.color} variant="outlined"
              sx={{ fontSize: 11, height: 22, fontWeight: 700 }} />
            {detail && <Box component="span" sx={{ fontSize: 12, opacity: 0.7 }}>{detail}</Box>}
          </Box>
        );
      },
    },
    { field: "description", headerName: "Descripción", flex: 1.5, minWidth: 200 },
    { field: "sort_order",  headerName: "Posición", width: 90 },
    {
      field: "is_active", headerName: "Estado", width: 110,
      renderCell: ({ value }) => (
        <Chip label={value ? "Visible" : "Oculta"} size="small"
          color={value ? "success" : "default"} variant="outlined"
          sx={{ fontSize: 11, height: 22, fontWeight: 700 }} />
      ),
    },
    {
      field: "actions", headerName: "Acciones", width: 100, sortable: false,
      renderCell: ({ row }) => (
        <Box sx={{ display: "flex", gap: 0.5 }}>
          <IconButton size="small" onClick={() => openEdit(row)}>
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => { setRowToDelete(row); setConfirmDeleteOpen(true); }}>
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
  ];

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <ActionHeaderCard
            icon={<DataObjectIcon />}
            title="Fuentes de Documentos"
            description="Archivos que se pueden adjuntar a un escrito al subirlo a SAC. Cada fuente visible aparece como opción en «Archivos adicionales a incluir en el escrito»."
            action={
              <Button
                onClick={openCreate}
                variant="contained"
                startIcon={<AddIcon />}
                sx={{ borderRadius: 2, fontWeight: 800, textTransform: "none" }}
              >
                Nueva fuente
              </Button>
            }
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <DataGridCustom
            rows={rows} columns={columns} loading={loading} error={error}
            success={success} onCloseSuccess={() => setSuccess("")}
          />
        </Grid>
      </Grid>

      <AppDialog open={dialogOpen} onClose={() => setDialogOpen(false)}
        title={editRow ? "Editar fuente" : "Nueva fuente de documentos"}
        maxWidth="sm" fullWidth
        actions={
          <>
            <Button onClick={() => setDialogOpen(false)} disabled={saving}>Cancelar</Button>
            <Button variant="contained" onClick={handleSave} disabled={saving}
              startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon />}
              sx={{ fontWeight: 800 }}>
              Guardar
            </Button>
          </>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 0.5 }}>
          <TextField label="Nombre del archivo *" size="small" value={form.label}
            onChange={(e) => setField("label", e.target.value)}
            error={!!formErrors.label}
            helperText={formErrors.label ?? "Así lo van a ver los operadores al subir el escrito y en los adjuntos del expediente. Ej: Poder General"}
            fullWidth autoFocus />

          <FormControl size="small" fullWidth>
            <InputLabel>Tipo</InputLabel>
            <Select value={form.type} label="Tipo" onChange={(e) => setField("type", e.target.value)}>
              {typeOptions.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
            <Typography variant="caption" sx={{ mt: 0.5, ml: 1.5, opacity: 0.7 }}>
              Agrupa el archivo junto a los de su tipo al elegir qué adjuntar al escrito.
            </Typography>
          </FormControl>

          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              ¿De dónde sale el archivo?
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              {ORIGINS.map((o) => {
                const selected = origin === o.value;
                return (
                  <Box key={o.value} onClick={() => setOrigin(o.value)}
                    sx={{
                      display: "flex", gap: 1.5, p: 1.5, borderRadius: 2, cursor: "pointer",
                      border: "1px solid",
                      borderColor: selected ? "primary.main" : "divider",
                      bgcolor: selected ? "action.selected" : "transparent",
                      "&:hover": { bgcolor: selected ? "action.selected" : "action.hover" },
                    }}>
                    <Box sx={{ color: selected ? "primary.main" : "text.secondary", pt: 0.2 }}>{o.icon}</Box>
                    <Box>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{o.label}</Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>{o.hint}</Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Box>

          {origin === "legal_file_document" && !editRow && (
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              Los documentos que se carguen en el expediente con este tipo van a figurar como «{form.label.trim() || "…"}».
            </Typography>
          )}

          {origin === "lawyer_field" && (
            <FormControl size="small" fullWidth error={!!formErrors.relation_path}>
              <InputLabel>Archivo del abogado *</InputLabel>
              <Select value={form.relation_path} label="Archivo del abogado *"
                onChange={(e) => setField("relation_path", e.target.value)}>
                {lawyerFields.map((f) => (
                  <MenuItem key={f.value} value={f.value}>{f.label}</MenuItem>
                ))}
              </Select>
              {formErrors.relation_path && (
                <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                  {formErrors.relation_path}
                </Typography>
              )}
            </FormControl>
          )}

          <TextField label="Descripción (opcional)" size="small" value={form.description}
            onChange={(e) => setField("description", e.target.value)}
            fullWidth multiline maxRows={3}
            helperText="Para qué sirve o en qué escritos se usa." />

          <Box sx={{ display: "flex", gap: 2, alignItems: "center", flexWrap: "wrap" }}>
            <FormControl size="small" sx={{ minWidth: 200, flex: 1 }}>
              <InputLabel>Ícono</InputLabel>
              <Select value={form.icon_key} onChange={(e) => setField("icon_key", e.target.value)} label="Ícono">
                {ICON_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>{opt.icon}{opt.label}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Posición en la lista" size="small" type="number" value={form.sort_order}
              onChange={(e) => setField("sort_order", Number(e.target.value))}
              sx={{ width: 150 }} inputProps={{ min: 0 }} />
          </Box>

          <FormControlLabel
            control={<Switch checked={form.is_active} onChange={(e) => setField("is_active", e.target.checked)} size="small" />}
            label={<Typography variant="body2">Mostrar en la lista de archivos del escrito</Typography>} />

          <Box>
            <Button size="small" onClick={() => setShowTechnical((v) => !v)}
              endIcon={<ExpandMoreIcon sx={{ transform: showTechnical ? "rotate(180deg)" : "none", transition: "0.2s" }} />}
              sx={{ textTransform: "none", px: 0, opacity: 0.7 }}>
              Datos técnicos (solo sistemas)
            </Button>
            <Collapse in={showTechnical}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.8, pt: 1 }}>
                <TextField label="Identificador interno" size="small"
                  value={form.source_type}
                  onChange={(e) => setField("source_type", e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_"))}
                  error={!!formErrors.source_type}
                  helperText={formErrors.source_type ?? (editRow ? "No se puede cambiar." : "Se genera solo a partir del nombre si lo dejás vacío.")}
                  fullWidth disabled={!!editRow} InputProps={{ sx: { fontFamily: "monospace" } }} />
                {origin === "advanced" && (
                  <>
                    <FormControl size="small" fullWidth>
                      <InputLabel>Tipo de relación</InputLabel>
                      <Select value={form.resolver_strategy} label="Tipo de relación"
                        onChange={(e) => setField("resolver_strategy", e.target.value)}>
                        {ADVANCED_STRATEGIES.map((st) => (
                          <MenuItem key={st.value} value={st.value}>{st.label}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <TextField label="Ruta del archivo *" size="small" value={form.relation_path}
                      onChange={(e) => setField("relation_path", e.target.value)}
                      error={!!formErrors.relation_path}
                      helperText={formErrors.relation_path ?? 'Relaciones desde el expediente separadas por punto. Ej: "lawsuits.storage_path"'}
                      fullWidth InputProps={{ sx: { fontFamily: "monospace" } }} />
                  </>
                )}
                {origin === "legal_file_document" && editRow && (
                  <TextField label="Tipo de documento" size="small" value={form.relation_path}
                    fullWidth disabled InputProps={{ sx: { fontFamily: "monospace" } }} />
                )}
              </Box>
            </Collapse>
          </Box>
        </Box>
      </AppDialog>

      <ConfirmDialog open={confirmDeleteOpen}
        onClose={() => { setConfirmDeleteOpen(false); setRowToDelete(null); }}
        onConfirm={handleDeleteConfirm}
        title="Eliminar fuente"
        description={`¿Eliminar "${rowToDelete?.label}"? Va a dejar de aparecer en la lista de archivos de los escritos que la usen.`}
        confirmText="Eliminar" confirmColor="error" loading={deleting} />
    </Container>
  );
}

export default function SettingsAttachmentSources() {
  const { auth } = useAuth();

  if (auth.user?.id !== 1) {
    return <Navigate to="/home" replace />;
  }

  return <AttachmentSourceTypesContent />;
}
